import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, parseISO, addDays, isWeekend } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Pencil, Trash2, Users, CalendarDays, CreditCard, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, token, isAdmin, dbUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [trainers, setTrainers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Trainer form
  const [trainerDialogOpen, setTrainerDialogOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [trainerForm, setTrainerForm] = useState({
    name: '',
    photo_url: '',
    specialty: ''
  });

  // Slot generation - 1h slots
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotTimes, setSlotTimes] = useState([
    { start: '09:00', end: '10:00' },
    { start: '10:00', end: '11:00' },
    { start: '11:00', end: '12:00' },
    { start: '14:00', end: '15:00' },
    { start: '15:00', end: '16:00' },
    { start: '16:00', end: '17:00' },
    { start: '17:00', end: '18:00' }
  ]);
  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [trainersRes, bookingsRes, slotsRes] = await Promise.all([
        axios.get(`${API}/trainers/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/slots/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTrainers(trainersRes.data);
      setBookings(bookingsRes.data);
      setSlots(slotsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait for auth to load before checking admin status
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (user && !isAdmin && dbUser) {
      toast.error('Accès réservé aux administrateurs');
      navigate('/');
      return;
    }

    if (token && isAdmin) {
      fetchData();
    }
  }, [authLoading, user, isAdmin, dbUser, token, navigate, fetchData]);



  // Trainer CRUD
  const handleTrainerSubmit = async () => {
    try {
      const data = {
        ...trainerForm,
        hourly_rate: 20  // Default rate, not used anymore but required by backend
      };

      if (editingTrainer) {
        await axios.put(
          `${API}/trainers/${editingTrainer.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coach modifié');
      } else {
        await axios.post(
          `${API}/trainers`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coach ajouté');
      }

      setTrainerDialogOpen(false);
      setEditingTrainer(null);
      setTrainerForm({ name: '', photo_url: '', specialty: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving trainer:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteTrainer = async (trainerId) => {
    try {
      await axios.delete(
        `${API}/trainers/${trainerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coach supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditTrainer = (trainer) => {
    setEditingTrainer(trainer);
    setTrainerForm({
      name: trainer.name,
      photo_url: trainer.photo_url,
      specialty: trainer.specialty
    });
    setTrainerDialogOpen(true);
  };

  // Slot generation
  const generateSlots = async () => {
    if (!selectedTrainerId || !selectedDate) {
      toast.error('Sélectionnez un coach et une date');
      return;
    }

    try {
      const slotsToCreate = slotTimes.map(time => ({
        trainer_id: selectedTrainerId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: time.start,
        end_time: time.end
      }));

      const response = await axios.post(
        `${API}/slots/bulk`,
        slotsToCreate,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.created} créneaux créés`);
      fetchData();
    } catch (error) {
      console.error('Error creating slots:', error);
      toast.error('Erreur lors de la création des créneaux');
    }
  };

  // Booking management
  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(
        `${API}/bookings/${bookingId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réservation annulée');
      fetchData();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const disabledDays = (date) => date < new Date();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-muted-foreground">Chargement...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="font-heading font-black text-4xl mb-2">Administration</h1>
            <p className="text-muted-foreground">
              Gérez les coachs, plannings et réservations
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4FF00] rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{trainers.length}</p>
                    <p className="text-sm text-muted-foreground">Coachs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <CalendarDays className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{bookings.filter(b => b.status !== 'cancelled').length}</p>
                    <p className="text-sm text-muted-foreground">Réservations actives</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {(bookings.filter(b => b.payment_status === 'paid' && b.status !== 'cancelled').reduce((sum, b) => sum + b.amount, 0) * 20).toFixed(0)}€
                    </p>
                    <p className="text-sm text-muted-foreground">Revenus (crédits utilisés)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="trainers" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
              <TabsTrigger value="trainers" data-testid="tab-trainers">Coachs</TabsTrigger>
              <TabsTrigger value="slots" data-testid="tab-slots">Planning</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings">Réservations</TabsTrigger>
            </TabsList>

            {/* Trainers Tab */}
            <TabsContent value="trainers">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading">Gestion des Coachs</CardTitle>
                  <Dialog open={trainerDialogOpen} onOpenChange={setTrainerDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        className="bg-[#D4FF00] text-black hover:bg-[#bfe600]"
                        onClick={() => {
                          setEditingTrainer(null);
                          setTrainerForm({ name: '', photo_url: '', specialty: '' });
                        }}
                        data-testid="add-trainer-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un coach
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingTrainer ? 'Modifier le coach' : 'Ajouter un coach'}
                        </DialogTitle>
                        <DialogDescription>
                          Remplissez les informations du coach
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label htmlFor="name">Nom</Label>
                          <Input
                            id="name"
                            value={trainerForm.name}
                            onChange={(e) => setTrainerForm({ ...trainerForm, name: e.target.value })}
                            data-testid="trainer-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="photo">URL de la photo</Label>
                          <Input
                            id="photo"
                            value={trainerForm.photo_url}
                            onChange={(e) => setTrainerForm({ ...trainerForm, photo_url: e.target.value })}
                            placeholder="https://..."
                            data-testid="trainer-photo-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="specialty">Spécialité</Label>
                          <Input
                            id="specialty"
                            value={trainerForm.specialty}
                            onChange={(e) => setTrainerForm({ ...trainerForm, specialty: e.target.value })}
                            placeholder="Ex: Technique, Gardien, Fitness..."
                            data-testid="trainer-specialty-input"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setTrainerDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button
                          onClick={handleTrainerSubmit}
                          className="bg-[#D4FF00] text-black hover:bg-[#bfe600]"
                          data-testid="save-trainer-btn"
                        >
                          {editingTrainer ? 'Modifier' : 'Ajouter'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coach</TableHead>
                        <TableHead>Spécialité</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trainers.map((trainer) => (
                        <TableRow key={trainer.id} data-testid={`trainer-row-${trainer.id}`}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <img
                                src={trainer.photo_url}
                                alt={trainer.name}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://images.pexels.com/photos/1618042/pexels-photo-1618042.jpeg';
                                }}
                              />
                              {trainer.name}
                            </div>
                          </TableCell>
                          <TableCell>{trainer.specialty}</TableCell>
                          <TableCell>
                            <Badge variant={trainer.is_active ? "default" : "secondary"}>
                              {trainer.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditTrainer(trainer)}
                                data-testid={`edit-trainer-${trainer.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive"
                                    data-testid={`delete-trainer-${trainer.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Supprimer ce coach ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Cette action est irréversible.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteTrainer(trainer.id)}
                                      className="bg-destructive text-destructive-foreground"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Slots Tab */}
            <TabsContent value="slots">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Gestion du Planning</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div>
                      <Label className="text-base font-medium mb-4 block">1. Sélectionner un coach</Label>
                      <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
                        <SelectTrigger data-testid="select-trainer-slots">
                          <SelectValue placeholder="Choisir un coach" />
                        </SelectTrigger>
                        <SelectContent>
                          {trainers.filter(t => t.is_active).map((trainer) => (
                            <SelectItem key={trainer.id} value={trainer.id}>
                              {trainer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Label className="text-base font-medium mb-4 block mt-6">2. Sélectionner une date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={disabledDays}
                        locale={fr}
                        className="rounded-md border w-fit"
                        data-testid="slot-calendar"
                      />
                    </div>

                    <div>
                      <Label className="text-base font-medium mb-4 block">3. Créneaux à générer</Label>
                      <div className="space-y-2 mb-6">
                        {slotTimes.map((time, index) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-secondary rounded-lg">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{time.start} - {time.end}</span>
                            <Badge variant="outline" className="ml-auto">1h</Badge>
                          </div>
                        ))}
                      </div>

                      <Button
                        onClick={generateSlots}
                        className="w-full bg-[#D4FF00] text-black hover:bg-[#bfe600] font-bold"
                        disabled={!selectedTrainerId || !selectedDate}
                        data-testid="generate-slots-btn"
                      >
                        Générer les créneaux
                      </Button>

                      {selectedDate && (
                        <p className="text-sm text-muted-foreground mt-4 text-center">
                          Pour le {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Toutes les Réservations</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Coach</TableHead>
                        <TableHead>Date & Heure</TableHead>
                        <TableHead>Valeur</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                          <TableCell className="font-medium">{booking.user_email}</TableCell>
                          <TableCell>{booking.trainer_name}</TableCell>
                          <TableCell>
                            <div>
                              <p>{format(parseISO(booking.date), 'd MMM yyyy', { locale: fr })}</p>
                              <p className="text-sm text-muted-foreground">
                                {booking.start_time} - {booking.end_time}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>{(booking.amount * 20).toFixed(0)}€</TableCell>
                          <TableCell>
                            {booking.status === 'cancelled' ? (
                              <Badge variant="destructive">Annulée</Badge>
                            ) : booking.payment_status === 'paid' ? (
                              <Badge className="bg-green-500">Confirmée</Badge>
                            ) : (
                              <Badge variant="secondary">En attente</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {booking.status !== 'cancelled' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    data-testid={`admin-cancel-${booking.id}`}
                                  >
                                    Annuler
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Annuler cette réservation ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Le client sera notifié et le créneau sera libéré.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Non</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleCancelBooking(booking.id)}
                                      className="bg-destructive text-destructive-foreground"
                                    >
                                      Oui, annuler
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
